/**
 * TransportControls - play/pause/stop and BPM controls
 *
 * Transport is local (client-side audio).
 */

import { useState, useCallback } from 'react';
import { useTransport } from '../hooks/useTransport';
import { initWebAudioContext } from '@/env/web/AudioContext';

export function TransportControls() {
  const [transportState, setTransportState] = useState<'started' | 'stopped' | 'paused'>('stopped');
  const [bpm, setBpm] = useState(120);
  const transport = useTransport();

  const handlePlay = useCallback(async () => {
    await initWebAudioContext();
    transport.play();
    setTransportState('started');
  }, [transport]);

  const handlePause = useCallback(() => {
    transport.pause();
    setTransportState('paused');
  }, [transport]);

  const handleStop = useCallback(() => {
    transport.stop();
    setTransportState('stopped');
  }, [transport]);

  const handleBpmChange = useCallback((newBpm: number) => {
    setBpm(newBpm);
    transport.setBpm(newBpm);
  }, [transport]);

  return (
    <div style={styles.transport}>
      <button
        onClick={transportState === 'started' ? handlePause : handlePlay}
        style={{
          ...styles.button,
          background: transportState === 'started' ? '#f59e0b' : '#22c55e'
        }}
      >
        {transportState === 'started' ? '⏸' : '▶'}
      </button>
      <button
        onClick={handleStop}
        disabled={transportState === 'stopped'}
        style={{
          ...styles.button,
          background: transportState === 'stopped' ? '#334155' : '#ef4444',
          opacity: transportState === 'stopped' ? 0.4 : 1,
          cursor: transportState === 'stopped' ? 'default' : 'pointer',
        }}
      >
        ⏹
      </button>
      <div style={styles.bpmControl}>
        <input
          type="range"
          min="20"
          max="340"
          value={bpm}
          onChange={(e) => handleBpmChange(Number(e.target.value))}
          style={styles.slider}
        />
        <span style={styles.bpmValue}>{Math.round(bpm)}</span>
        <span style={styles.bpmLabel}>BPM</span>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  transport: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  button: {
    width: 36,
    height: 36,
    borderRadius: 6,
    border: 'none',
    color: 'white',
    fontSize: 16,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  bpmControl: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  slider: {
    width: 80,
    cursor: 'pointer',
  },
  bpmValue: {
    fontSize: 14,
    fontWeight: 600,
    minWidth: 30,
    textAlign: 'right' as const,
  },
  bpmLabel: {
    fontSize: 12,
    color: '#888',
  },
};
